/**
 * Chart colours, read from the design tokens rather than written here.
 *
 * `tokens.css` is the only file in the client allowed to contain a colour, and
 * a chart library is not an exception — a redesign that changed the palette and
 * left the charts on the old one would look broken. These read the custom
 * properties off the document at runtime, so the charts follow the tokens
 * wherever they end up.
 *
 * On accessibility: **never colour alone.** Every series here also carries a
 * label, and the capability scale is ordered light to dark so its four levels
 * are distinguishable in greyscale and to a colour-blind reader. That is a
 * requirement of the phase, not a nicety — a supervisor may print this.
 */

import { useEffect, useState } from 'react';

import { onThemeChange } from '../../lib/theme.ts';

function token(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;

  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

export type ChartTheme = {
  axis: string;
  grid: string;
  text: string;
  muted: string;
  surface: string;
  /** Level 1 to level 4, deliberately ordered from pale to strong. */
  capability: [string, string, string, string];
  /** For categorical series — procedures, case types, reasons. */
  categorical: string[];
};

export function chartTheme(): ChartTheme {
  return {
    axis: token('--ui-chart-axis', '#949ca7'),
    grid: token('--ui-chart-grid', '#2e333b'),
    text: token('--ui-text', '#e9ebee'),
    muted: token('--ui-text-muted', '#abb2bc'),
    surface: token('--ui-surface', '#1d2025'),
    /*
     * A single hue at four strengths, not four hues. Capability level is an
     * ordered scale, and rendering an ordered scale in categorical colours
     * invites a reader to see four unrelated things rather than a progression.
     *
     * The scale is monotonic in lightness in both themes, and its direction
     * reverses between them so level 4 is always the most emphatic against its
     * own background. Steps are 15–18 L* apart, which survives a photocopier.
     */
    capability: [
      token('--ui-capability-1', '#3a5a78'),
      token('--ui-capability-2', '#54809f'),
      token('--ui-capability-3', '#84aed4'),
      token('--ui-capability-4', '#c2dbf0'),
    ],
    /*
     * Eight hues that are nobody else's. This deliberately reuses no status
     * colour: the previous palette borrowed success, warning and danger, so a
     * doughnut slice could read as an error or a warning purely by accident of
     * where it fell in the sort order.
     *
     * Assign by category index, never by value rank — see `seriesColour`.
     */
    categorical: [
      token('--ui-chart-1', '#5b93d1'),
      token('--ui-chart-2', '#d08f66'),
      token('--ui-chart-3', '#9cb05f'),
      token('--ui-chart-4', '#dda54a'),
      token('--ui-chart-5', '#b3a6ee'),
      token('--ui-chart-6', '#68c6bf'),
      token('--ui-chart-7', '#efb2aa'),
      token('--ui-chart-8', '#aee0c1'),
    ],
  };
}

/**
 * Re-reads the tokens whenever the resolved theme changes.
 *
 * The charts read their colours off the document at runtime, which is what lets
 * a token edit restyle them — but `getComputedStyle` is a snapshot, so a theme
 * switch leaves Recharts holding the old palette until something else forces a
 * render. This is the subscription that forces it.
 */
export function useChartTheme(): ChartTheme {
  const [theme, setTheme] = useState<ChartTheme>(chartTheme);

  useEffect(() => onThemeChange(() => setTheme(chartTheme())), []);

  return theme;
}

/** Everything past the eighth slice, collected rather than dropped. */
export const OTHER_LABEL = 'Other';
export const TOP_SLICES = 8;

export function topWithOther<T extends { label: string; count: number }>(
  rows: readonly T[],
  limit = TOP_SLICES,
): { label: string; count: number }[] {
  const ranked = [...rows].filter((row) => row.count > 0).sort((a, b) => b.count - a.count);
  if (ranked.length <= limit) return ranked.map((row) => ({ label: row.label, count: row.count }));

  const head = ranked.slice(0, limit).map((row) => ({ label: row.label, count: row.count }));
  const tail = ranked.slice(limit).reduce((sum, row) => sum + row.count, 0);

  return [...head, { label: OTHER_LABEL, count: tail }];
}
